import api from './axiosConfig';

export interface UsageSummary {
  total_requests: number;
  total_tokens: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_image_count: number;
  total_cost_usd: number;
  total_credits: number;
}

export interface FeatureUsage {
  feature_type: string;
  feature_label: string;
  total_requests: number;
  total_tokens: number;
  total_image_count: number;
  total_cost_usd: number;
  total_credits: number;
}

export interface OrganizationUsageItem {
  organization_id: number;
  organization_name: string;
  total_requests: number;
  total_tokens: number;
  total_cost_usd: number;
  total_credits: number;
}

export interface UserUsageItem {
  user_id: number;
  user_name: string;
  total_requests: number;
  total_tokens: number;
  total_cost_usd: number;
  total_credits: number;
}

export interface DailyTrend {
  date: string;
  total_requests: number;
  total_tokens: number;
  total_cost_usd: number;
  total_credits: number;
}

export interface ActivationFunnelStep {
  key: string;
  label: string;
  users: number;
  rate: number;
  dropoff_from_previous: number;
}

export interface ActivationFeatureReach {
  key: string;
  label: string;
  users: number;
  rate: number;
}

export interface ActivationCohortTrend {
  date: string;
  signup_users: number;
  activated_users: number;
  activation_rate: number;
}

export interface ActivationOrganizationItem {
  organization_id: number | null;
  organization_name: string;
  signup_users: number;
  activated_users: number;
  activation_rate: number;
}

export interface ActivationFunnelData {
  summary: {
    signup_users: number;
    activated_users: number;
    activation_rate: number;
    largest_dropoff_step: {
      key: string;
      label: string;
      dropoff_from_previous: number;
    } | null;
  };
  funnel_steps: ActivationFunnelStep[];
  feature_reach: ActivationFeatureReach[];
  cohort_trend: ActivationCohortTrend[];
  by_organization: ActivationOrganizationItem[];
  event_definitions: Array<{ key: string; source: string }>;
}

export interface FeatureTrendItem {
  date: string;
  features: Record<string, number>;
}

export interface UsageOverviewResponse {
  success: boolean;
  data: {
    summary: UsageSummary;
    by_feature: FeatureUsage[];
    by_organization: OrganizationUsageItem[];
    daily_trend: DailyTrend[];
    activation_funnel?: ActivationFunnelData;
  };
  period: {
    type: string;
    start_date: string;
    end_date: string;
  };
}

export interface OrganizationUsageResponse {
  success: boolean;
  data: {
    organization: {
      id: number;
      name: string;
    };
    summary: UsageSummary;
    by_feature: FeatureUsage[];
    by_user: UserUsageItem[];
    daily_trend: DailyTrend[];
    feature_trend: FeatureTrendItem[];
  };
  period: {
    type: string;
    start_date: string;
    end_date: string;
  };
}

export interface RecentLog {
  id: number;
  feature_type: string;
  feature_label: string;
  model_name: string;
  total_tokens: number;
  estimated_cost_usd: number;
  created_at: string;
}

export interface UserUsageResponse {
  success: boolean;
  data: {
    user: {
      id: number;
      name: string;
      email: string;
      organization_id: number | null;
    };
    summary: UsageSummary;
    by_feature: FeatureUsage[];
    daily_trend: DailyTrend[];
    recent_logs: RecentLog[];
  };
  period: {
    type: string;
    start_date: string;
    end_date: string;
  };
}

export type PeriodType = 'day' | 'week' | 'month' | 'year';

const adminUsageService = {
  /**
   * Get overall AI usage overview
   */
  async getOverview(period: PeriodType = 'month'): Promise<UsageOverviewResponse> {
    const response = await api.get<UsageOverviewResponse>('/admin/usage/overview', {
      params: { period }
    });
    return response.data;
  },

  /**
   * Get AI usage for a specific organization
   */
  async getOrganizationUsage(organizationId: number, period: PeriodType = 'month'): Promise<OrganizationUsageResponse> {
    const response = await api.get<OrganizationUsageResponse>(`/admin/usage/organizations/${organizationId}`, {
      params: { period }
    });
    return response.data;
  },

  /**
   * Get AI usage for a specific user
   */
  async getUserUsage(userId: number, period: PeriodType = 'month'): Promise<UserUsageResponse> {
    const response = await api.get<UserUsageResponse>(`/admin/usage/users/${userId}`, {
      params: { period }
    });
    return response.data;
  },
};

export default adminUsageService;
